/**
 * Pagina di login della shell ERP Floraltech.
 *
 * Chiama POST /api/v1/auth/login e salva il JWT in localStorage.
 *
 * DEBITO TECNICO — localStorage:
 *   Il token JWT è attualmente salvato in localStorage per semplicità
 *   nella fase prototipo. Prima del passaggio alla fase SaaS, va sostituito
 *   con httpOnly cookie (Set-Cookie dal backend) per eliminare la superficie
 *   di attacco XSS sul token di autenticazione.
 *   Vedi BRAINSTORMING.md → "Sicurezza auth: localStorage → httpOnly cookie".
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { reload } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 2FA (secondo passo)
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);

  const finalizeLogin = async (accessToken: string) => {
    // DEBITO TECNICO: sostituire con httpOnly cookie prima della fase SaaS.
    localStorage.setItem('token', accessToken);
    await reload();
    navigate('/', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // OAuth2PasswordRequestForm richiede application/x-www-form-urlencoded
      const body = new URLSearchParams({ username: email, password });
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          (data as { detail?: string }).detail ?? `Errore ${response.status}`
        );
      }

      const data = (await response.json()) as {
        access_token?: string;
        mfa_required?: boolean;
        challenge?: string;
      };

      if (data.mfa_required && data.challenge) {
        // Secondo fattore richiesto: mostra lo step del codice.
        setMfaChallenge(data.challenge);
        return;
      }

      if (data.access_token) {
        await finalizeLogin(data.access_token);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2fa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaChallenge) return;
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, string> = { challenge: mfaChallenge };
      if (useRecovery) payload.recovery_code = mfaCode.trim();
      else payload.codice = mfaCode.trim();
      const response = await fetch('/api/v1/auth/2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as { detail?: string }).detail ?? `Errore ${response.status}`);
      }
      const { access_token } = (await response.json()) as { access_token: string };
      await finalizeLogin(access_token);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-light dark:bg-neutral-dark">
      <div className="w-full max-w-sm">
        {/* Logo / intestazione */}
        <div className="mb-8 text-center">
          <h1 className="font-outfit text-3xl font-bold text-primary dark:text-neutral-light">
            {t('login.title')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mfaChallenge ? t('login.mfaSubtitle') : t('login.subtitle')}
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-8 shadow-sm">
          {!mfaChallenge ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-foreground"
                >
                  {t('login.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder=""
                />
              </div>

              <div className="space-y-1">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-foreground"
                >
                  {t('login.password')}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors duration-150 ease-erp hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? t('login.submitting') : t('login.submit')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify2fa} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="mfacode" className="block text-sm font-medium text-foreground">
                  {useRecovery ? t('login.recoveryCode') : t('login.mfaCode')}
                </label>
                <input
                  id="mfacode"
                  type="text"
                  inputMode={useRecovery ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  required
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {error && (
                <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors duration-150 ease-erp hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? t('login.submitting') : t('login.mfaSubmit')}
              </button>

              <button
                type="button"
                onClick={() => { setUseRecovery((v) => !v); setMfaCode(''); setError(null); }}
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
              >
                {useRecovery ? t('login.useCode') : t('login.useRecovery')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
